import React from "react";

import EmptyChart from "../../../../../../components/EmptyChart";
import Spinner from "../../../../../../components/Spinner/QuarterCircle";
import Text from "../../../../../../components/Text";
// components
import { CardBody, CardHeader } from "../../../../components/Card";
import ScatterChart from "../../../../components/ScatterChart";
// hooks
import useScatterChart from "../../../../components/ScatterChart/useScatterChart";
// local components
import ComparisonDropdowns from "./ComparisonDropdowns";

// store
import useStore from "../../../../store";

import { useFactorComparison, usePageSettings, useUpdatePageSettings } from "../../../../../../api/hooks";

// config
import Config from "../../../../Config";
// schema
import { IFactorComparisonPoint, ScatterAxis } from "../../../../schema";
// css
import css from "./FactorComparison.module.scss";

/**
 * Renders a factor comparison scatetr chart inside a card
 * with options to change the x and y axis
 */
const FactorComparison = () => {
	const [filter] = useStore((state) => [state.filter]);

	// derive axes from page settings
	const pageSettings = usePageSettings();

	const xAxis = pageSettings.data?.factors?.scatter?.xAxis || "Opportunity";
	const yAxis = pageSettings.data?.factors?.scatter?.yAxis || "all";

	// setAxis mutations
	const setPageSettings = useUpdatePageSettings().mutate;
	const setXAxis = (axis: ScatterAxis) => {
		setPageSettings({
			settings_path: "factors.scatter.xAxis",
			settings_value: axis,
		});
	};
	const setYAxis = (axis: ScatterAxis) => {
		setPageSettings({
			settings_path: "factors.scatter.yAxis",
			settings_value: axis,
		});
	};

	const { data, isLoading } = useFactorComparison({
		xAxis,
		yAxis,
		startDate: filter.startDate ? filter.startDate : undefined,
		endDate: filter.endDate,
	});

	const labelsMap = [...Config.FACTORS.FACTORS, "all", "eNPS"].reduce<Record<ScatterAxis, string>>(
		(acc, curr) => {
			if (curr === "all") {
				acc[curr] = "Satisfaction";
			} else if (curr === "eNPS") {
				acc[curr] = "eNPS Rating";
			} else {
				acc[curr] = curr;
			}
			return acc;
		},
		{} as Record<ScatterAxis, string>
	);

	const validData = data || ({} as IFactorComparisonPoint[]);
	const { options, prepareDataForChart, getScaledOptions } = useScatterChart({
		xAxisLabel: labelsMap[xAxis],
		yAxisLabel: labelsMap[yAxis],
		data: validData,
	});

	/**
	 * returns the options for an eNPS axis to be used when either axis selects eNPS
	 */
	const getEnpsOptions = (axis: "x" | "y") => {
		return {
			...(data?.length
				? {}
				: // shows full scale when no enps data and enps selected
					{
						min: 0,
						max: 10,
					}),
			// and eNPS always gets increased step size
			ticks: {
				...(options.scales?.[axis]?.ticks || {}),
				stepSize: 2,
			},
		};
	};

	/**
	 * Scaled options adjusts the scaling and step size of the chart when the
	 * axis is eNPS
	 */
	const scaledOptions = getScaledOptions(
		xAxis === "eNPS"
			? getEnpsOptions("x")
			: // default to 1-5 scale
				{ min: 1, max: 5 },
		yAxis === "eNPS"
			? getEnpsOptions("y")
			: // default to 1-5 scale
				{ min: 1, max: 5 }
	);

	return (
		<>
			<CardHeader
				title={
					<span>
						<Text element="span" size="paragraph-2" weight="semi-bold">
							Factor Comparison
						</Text>
					</span>
				}
				info="This chart displays individual points for each anonymous employee comparing their satisfaction factors for the selected period."
				toolBar={<ComparisonDropdowns xAxis={xAxis} yAxis={yAxis} setXAxis={setXAxis} setYAxis={setYAxis} />}
			/>
			<CardBody>
				<div className={css.cardBody}>
					{isLoading && (
						<div className={css.spinner}>
							<Spinner />
						</div>
					)}
					{!isLoading && data && data.length <= 0 && (
						<div className={css.emptyChartContainer}>
							<EmptyChart type="scatterLarge" />
						</div>
					)}
					{!isLoading && data && data.length > 0 && (
						<div className={css.scatterChart}>
							<ScatterChart data={prepareDataForChart()} options={scaledOptions} />
						</div>
					)}
				</div>
			</CardBody>
		</>
	);
};

export default FactorComparison;
